import type { RunEtaPayload } from "@the-drop/types";
import { useEffect, useState } from "react";
import { useTransporterSocketInstance } from "../workspace/transporter-socket-context";

/**
 * Listens for **`run.eta`** on the shared transporter socket and accumulates the latest payload per
 * `runId`. Returns a snapshot map the active-runs UI can index by `run.runId` to surface
 * stops-ahead + per-order ETA windows inline.
 *
 * **Audience scoping reminder:** `run.eta` is in `CUSTOMER_NAMESPACE_DENYLIST` (PR #374) — it
 * never reaches customer namespaces, so consuming it here on the transporter socket is safe.
 *
 * Mirrors dispensary's `useDispensaryRunEta`. Both hooks have the same shape because the
 * underlying contract (`RunEtaPayload` from `@the-drop/types`) is the same; only the socket
 * provider differs.
 */
export function useTransporterRunEta(
  transporterId: string | null,
): Record<string, RunEtaPayload> {
  const socket = useTransporterSocketInstance();
  const [runEtaByRunId, setRunEtaByRunId] = useState<Record<string, RunEtaPayload>>({});

  useEffect(() => {
    if (!socket || !transporterId) return;

    const onRunEta = (raw: unknown) => {
      const p = raw as Partial<RunEtaPayload> | null;
      if (
        p == null ||
        typeof p.runId !== "string" ||
        !Array.isArray(p.stops) ||
        typeof p.computedAt !== "string"
      ) {
        return;
      }
      const runId = p.runId;
      const accepted: RunEtaPayload = {
        runId,
        dispensaryId: typeof p.dispensaryId === "string" ? p.dispensaryId : undefined,
        transporterId: typeof p.transporterId === "string" ? p.transporterId : undefined,
        stops: p.stops.map((s) => ({
          orderId: String(s.orderId ?? ""),
          stopsAhead: Number(s.stopsAhead ?? 0),
          etaStart: String(s.etaStart ?? ""),
          etaEnd: String(s.etaEnd ?? ""),
        })),
        computedAt: p.computedAt,
      };
      setRunEtaByRunId((prev) => ({ ...prev, [runId]: accepted }));
    };

    socket.on("run.eta", onRunEta);
    return () => {
      socket.off("run.eta", onRunEta);
    };
  }, [socket, transporterId]);

  return runEtaByRunId;
}
